import Image from 'next/image';
import { cn } from '@sharpr/ui';
import { SHARPR_WORDMARK_DIMS } from '@/lib/brand';

/** Marketing-surface primitives shared by the landing page and /pricing so the two can't drift. */

/** Frosted card chrome — demo slate, Discord feed, final CTA, image frames. */
export const GLASS_CARD =
  'overflow-hidden rounded-xl border border-[var(--glass-border)] bg-[hsl(var(--bg))]/60 dark:bg-[var(--bg-secondary)]/60 shadow-card';

/** Sticky translucent header chrome shared by the marketing pages. */
export const MARKETING_HEADER_CLASS =
  'sticky top-0 z-50 backdrop-blur-xl border-[var(--glass-border)] dark:border-[var(--glass-border)]';

/** Floating callout pill — hero screenshot annotations, demo click hints. */
export const FLOATING_PILL_CLASS = cn(
  'bg-white dark:bg-[var(--bg-secondary)]',
  'border border-[var(--glass-border)] shadow-card',
  'font-semibold text-fg whitespace-nowrap',
);

/** Inline Sharpr wordmark for headlines; callers pass height + baseline-align classes.
 *  Intrinsic width/height (SHARPR_WORDMARK_DIMS) let the browser reserve the
 *  aspect-ratio box before the image decodes — without them the img is 0px wide
 *  at first paint and the headline text shifts when it loads (measured CLS
 *  contributor on / and /pricing). */
export function SharprWordmark({ className }: { className?: string }) {
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src="/sharpr.png"
      alt="Sharpr"
      {...SHARPR_WORDMARK_DIMS}
      className={cn('inline-block w-auto', className)}
    />
  );
}

/**
 * Odds-screen product proof, shared by the landing hero (eager + callout children)
 * and the pricing hero (lazy, capped height with a bottom fade). Children render
 * outside the clipped frame so callouts can overhang the edges.
 */
export function OddsScreenshotFrame({
  eager = false,
  fade = false,
  frameClassName,
  children,
}: {
  eager?: boolean;
  fade?: boolean;
  frameClassName?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="relative w-full">
      <div className={cn(GLASS_CARD, frameClassName)}>
        <div className={cn(fade && 'relative max-h-[360px] overflow-hidden')}>
          <Image
            src="/odds-screen-v12.webp"
            alt="Sharpr odds comparison screen showing player props with EV calculations across sportsbooks"
            width={3436}
            height={1970}
            sizes="(max-width: 1200px) 80vw, 1200px"
            className="block w-full h-auto"
            {...(eager
              ? { fetchPriority: 'high' as const, loading: 'eager' as const }
              : { loading: 'lazy' as const })}
          />
          {fade && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0 h-20 lg:h-24 bg-gradient-to-t from-white dark:from-bg-secondary via-white/80 dark:via-bg-secondary/80 to-transparent"
            />
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

// brand-* tokens are plain hex vars (no <alpha-value>), so slash modifiers like
// text-brand-500/80 silently drop the declaration — use plain class + opacity utility.
export function SectionEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-semibold tracking-[0.2em] uppercase text-brand-500 opacity-80">
      {children}
    </p>
  );
}

export function SectionTitle({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <h2 className={cn('text-3xl md:text-4xl font-bold tracking-tight text-fg', className)}>
      {children}
    </h2>
  );
}

/** Hero subtitle's two anchor links — same on both pages; wrapping <p> stays per-page. */
export function HeroAnchorLinks() {
  return (
    <>
      <a href="#projections-showcase" className="hover:text-fg transition-colors">
        Player prop projections
      </a>{' '}
      <span className="text-fg/40">·</span>{' '}
      <a href="#nba-research" className="hover:text-fg transition-colors">
        Deep player research
      </a>
    </>
  );
}
